import { useTranslation } from 'react-i18next';
import type { CatalogTree, TestCaseListItem } from '../../lib/catalog-api.js';
import {
  type CatalogFilter,
  fileOptions,
  hasAnyFilter,
  testerOptions,
} from '../../lib/catalog-filter.js';
import { pickLang } from '../../lib/pick-lang.js';

// #143: Filterbereich unter dem Baumkopf — Teilprojekt, Prozess, Standard-Tester, Import-Datei und
// „nur mit fehlender Übersetzung". Der Auf-/Zuklapp-Knopf samt Zähler liegt im Baumkopf
// (process-tree.tsx), weil der Zustand „offen" dort auch das Laden der vollen Testfallliste steuert.
// Die <select>-Klassen sind die des Defect-Boards (board-page.tsx), damit Filter überall gleich
// aussehen.

const SELECT_CLASS =
  'w-full min-w-0 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary';

export function CatalogFilterBar({
  tree,
  testCases,
  filter,
  onChange,
  onReset,
  loading = false,
  bulkFill,
}: {
  tree: CatalogTree | undefined;
  /** Volle Testfallliste — aus ihr entstehen die Tester- und Datei-Optionen. */
  testCases: TestCaseListItem[];
  filter: CatalogFilter;
  onChange: (patch: Partial<CatalogFilter>) => void;
  onReset: () => void;
  /** Testfallliste lädt noch: Tester/Datei haben dann noch keine Optionen und sind gesperrt. */
  loading?: boolean;
  /** #111: gesetzt → Knopf „Alle N ergänzen" in der Zeile des Lücken-Häkchens. Der Aufrufer setzt
   *  die Prop nur bei Lücken-Filter, Recht und mindestens einem Treffer. */
  bulkFill?: { count: number; onStart: () => void } | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';

  const nodes = tree?.nodes ?? [];
  // Prozess-Optionen hängen am gewählten Teilprojekt; ohne Teilprojekt sind es alle (inkl. „Ohne
  // Teilprojekt").
  const processesOf = (teilprojekt: string) =>
    teilprojekt === ''
      ? [...nodes.flatMap((n) => n.processes), ...(tree?.unlabeled ?? [])]
      : (nodes.find((n) => n.node_id === teilprojekt)?.processes ?? []);
  const processOptions = [...processesOf(filter.teilprojekt)].sort((a, b) =>
    a.name_de.localeCompare(b.name_de, 'de'),
  );

  const onTeilprojekt = (teilprojekt: string) => {
    // Ein Prozess, der im neuen Teilprojekt nicht liegt, würde den Baum leer filtern — im selben
    // Schritt zurücksetzen (ein URL-Schreibvorgang, siehe useCatalogFilter).
    const stillValid =
      filter.process === '' ||
      processesOf(teilprojekt).some((p) => p.process_id === filter.process);
    onChange(stillValid ? { teilprojekt } : { teilprojekt, process: '' });
  };

  const label = (key: string) => t(`catalog.filter.${key}`);
  const allOption = (key: string) => (
    <option value="">
      {label(key)}: {label('all')}
    </option>
  );

  return (
    <div className="space-y-2 border-b border-[var(--border)] px-3 py-2">
      <div className="grid grid-cols-2 gap-2">
        <select
          aria-label={label('teilprojekt')}
          value={filter.teilprojekt}
          onChange={(e) => onTeilprojekt(e.target.value)}
          className={SELECT_CLASS}
        >
          {allOption('teilprojekt')}
          {nodes.map((n) => (
            <option key={n.node_id} value={n.node_id}>
              {n.name}
            </option>
          ))}
        </select>
        <select
          aria-label={label('process')}
          value={filter.process}
          onChange={(e) => onChange({ process: e.target.value })}
          className={SELECT_CLASS}
        >
          {allOption('process')}
          {processOptions.map((p) => (
            <option key={p.process_id} value={p.process_id}>
              {pickLang(lang, p.name_de, p.name_en)}
            </option>
          ))}
        </select>
        <select
          aria-label={label('tester')}
          value={filter.tester}
          disabled={loading}
          onChange={(e) => onChange({ tester: e.target.value })}
          className={SELECT_CLASS}
        >
          {allOption('tester')}
          {testerOptions(testCases, label('noTester')).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label={label('file')}
          value={filter.file}
          disabled={loading}
          onChange={(e) => onChange({ file: e.target.value })}
          className={SELECT_CLASS}
        >
          {allOption('file')}
          {fileOptions(testCases, label('noFile')).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {/* flex-wrap: im schmalen Baumbereich (~380 px) passen Häkchen, „Alle N ergänzen" und
          „Filter zurücksetzen" nicht in eine Zeile — der rechte Block rutscht dann darunter. */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <label className="flex items-center gap-1.5 text-xs text-text-body">
          <input
            type="checkbox"
            checked={filter.gap}
            onChange={(e) => onChange({ gap: e.target.checked })}
          />
          {label('gap')}
        </label>
        <div className="ml-auto flex items-center gap-2">
          {bulkFill && (
            <button
              type="button"
              onClick={bulkFill.onStart}
              className="rounded-btn border border-[var(--border)] px-2 py-0.5 text-xs text-brand-accent hover:bg-brand-accent/10"
            >
              {t('catalog.translationFill.bulk.button', { count: bulkFill.count })}
            </button>
          )}
          {hasAnyFilter(filter) && (
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-brand-accent hover:underline"
            >
              {label('reset')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
